'use client';

import { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { useDebouncedSearchParam } from '@kit/ui/hooks/use-debounced-search-param';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { CategoryIcon } from '../sell/_components/wizard-steps/category-icon';
import { LocationFilter } from './location-filter';

export interface CategoryLeaf {
  slug: string;
  name: string;
  count: number;
  /** Kebab-case lucide name from commerce_categories.icon_name. */
  iconName?: string | null;
}

export interface CategoryGroup {
  slug: string;
  name: string;
  count: number;
  leaves: CategoryLeaf[];
  /** Kebab-case lucide name from commerce_categories.icon_name. */
  iconName?: string | null;
}

interface ListingFacetsProps {
  /**
   * Category taxonomy as selectable groups + leaf sub-categories, from
   * `CategoryService.getGroupedCategoriesForBrand` on the server (Critical Rule
   * #25: one edit surface per entity — the panel never hardcodes the taxonomy).
   * Category is single-select scope: a group selects all its leaves (resolved
   * server-side); a leaf selects itself.
   */
  groups: ReadonlyArray<CategoryGroup>;
  /** Distinct colour values present in the (category-scoped) catalogue. */
  colors: ReadonlyArray<string>;
  /** Distinct size values present in the (category-scoped) catalogue. */
  sizes: ReadonlyArray<string>;
  /**
   * Per-facet option counts (Baymard's highest-impact signal). Each facet's
   * counts reflect the other active facets but not its own. Null on the search
   * path (no exact count) → options render without counts.
   */
  counts: FacetCounts | null;
}

export type FacetCounts = {
  category: Record<string, number>;
  condition: Record<string, number>;
  color: Record<string, number>;
  size: Record<string, number>;
};

const CONDITIONS: ReadonlyArray<{ value: string; labelKey: string }> = [
  { value: 'new', labelKey: 'explore:marketplace.conditions.new' },
  { value: 'like_new', labelKey: 'explore:marketplace.conditions.likeNew' },
  { value: 'good', labelKey: 'explore:marketplace.conditions.good' },
  { value: 'fair', labelKey: 'explore:marketplace.conditions.fair' },
];

/**
 * Swatch fills for common dancewear colours. The value stored in the DB is the
 * key (lowercased); anything not mapped falls back to a neutral ring so the
 * label still carries the meaning (Baymard: never encode a facet by colour
 * alone — every swatch is paired with its text label).
 */
const COLOR_SWATCHES: Record<string, string> = {
  black: '#1a1a1a',
  white: '#f5f5f5',
  pink: '#f7b6c2',
  red: '#c0392b',
  burgundy: '#6d1f33',
  nude: '#e3c4a8',
  blue: '#3b5ba5',
  navy: '#1f2a44',
  green: '#3f7d52',
  purple: '#7d5ba6',
  grey: '#9aa0a6',
  gray: '#9aa0a6',
  brown: '#6b4f3a',
  beige: '#d8c3a5',
  gold: '#caa14a',
  silver: '#c4c8cc',
};

/**
 * Faceted filter panel for the marketplace browse page. Per the 2026 research
 * pass (Baymard/NN-g) the desktop layout is a persistent left-sidebar facet
 * panel rather than a horizontal toolbar — past 6-8 facets a horizontal bar
 * hides filters. This same component is rendered inside the mobile filter
 * drawer (`listing-filters-drawer.tsx`), so it must stay presentational and
 * URL-driven with no layout assumptions of its own.
 *
 * Every facet writes the URL (real-time on desktop, per Baymard) and resets the
 * keyset `cursor` so a new filter set starts from page 1. Search lives in its
 * own component (`listing-search.tsx`); this panel owns Category, Condition,
 * Brand, Colour, Size, Price. Search lives in `listing-search.tsx`.
 *
 * Category is a single-select scope: a collapsible group tree where the group
 * label selects all its leaves (resolved server-side) and a separate chevron
 * expands the leaves. Condition / Colour / Size are multi-select (OR within the
 * facet); Brand + Price are single inputs.
 */
export function ListingFacets({
  groups,
  colors,
  sizes,
  counts,
}: ListingFacetsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const toggleExpand = useCallback((slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('cursor');
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      const qs = params.toString();
      router.push(qs ? `/marketplace?${qs}` : '/marketplace');
    },
    [router, searchParams],
  );

  // Multi-select facets serialize as a comma-separated param (e.g.
  // `color=pink,black`). Toggling adds/removes one value (OR within facet).
  const toggleMulti = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('cursor');
      const current = (params.get(key) ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      if (next.length) {
        params.set(key, next.join(','));
      } else {
        params.delete(key);
      }
      const qs = params.toString();
      router.push(qs ? `/marketplace?${qs}` : '/marketplace');
    },
    [router, searchParams],
  );

  const splitParam = (key: string): string[] =>
    (searchParams.get(key) ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  // Free-text + numeric facets debounce their URL writes (the shared hook owns
  // the debounce + cursor reset); enum/taxonomy facets write immediately.
  const brand = useDebouncedSearchParam('brand', {
    debounceMs: 350,
    resetParams: ['cursor'],
  });
  const priceMin = useDebouncedSearchParam('priceMin', {
    debounceMs: 500,
    resetParams: ['cursor'],
  });
  const priceMax = useDebouncedSearchParam('priceMax', {
    debounceMs: 500,
    resetParams: ['cursor'],
  });

  const activeCategory = searchParams.get('category') ?? '';
  const activeConditions = splitParam('condition');
  const activeColors = splitParam('color');
  const activeSizes = splitParam('size');

  return (
    <div className="space-y-7" data-testid="listing-facets">
      {/* Location first (FB-Marketplace): for a pickup-heavy marketplace the
          radius is the highest-leverage scope filter. URL-driven like every
          other facet, so the mobile drawer gets it for free. */}
      <FacetGroup
        label={<Trans i18nKey="explore:marketplace.facets.location" />}
      >
        <LocationFilter />
      </FacetGroup>

      <FacetGroup
        label={<Trans i18nKey="explore:marketplace.facets.category" />}
      >
        <ul className="-mx-2 space-y-0.5">
          <li>
            <FacetRow
              active={!activeCategory}
              onClick={() => setParam('category', null)}
            >
              <Trans i18nKey="explore:marketplace.facets.allCategories" />
            </FacetRow>
          </li>
          {groups.map((g) => {
            const groupActive = activeCategory === g.slug;
            const leafActive = g.leaves.some((l) => l.slug === activeCategory);
            const isOpen = expanded.has(g.slug) || groupActive || leafActive;
            return (
              <li key={g.slug}>
                <div className="flex items-center gap-1">
                  {/* Label = select all leaves in the group (Baymard:
                      parent categories are selectable scope, not dead labels). */}
                  <Button
                    type="button"
                    variant="ghost"
                    aria-pressed={groupActive}
                    onClick={() =>
                      setParam('category', groupActive ? null : g.slug)
                    }
                    className={cn(
                      'min-w-0 flex-1 justify-start rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-normal transition-colors',
                      groupActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <CategoryIcon
                        name={g.iconName ?? null}
                        className="size-4 shrink-0"
                      />
                      <span className="min-w-0 flex-1 break-words">
                        {g.name}
                        {g.count > 0 && (
                          <span className="text-muted-foreground ml-1.5 text-xs font-normal tabular-nums">
                            {g.count}
                          </span>
                        )}
                      </span>
                    </span>
                  </Button>
                  {/* Separate chevron = expand/collapse (distinct affordance). */}
                  {g.leaves.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${g.name}`}
                      onClick={() => toggleExpand(g.slug)}
                      className="text-muted-foreground hover:text-foreground rounded-md p-1.5"
                    >
                      <ChevronDown
                        className={cn(
                          'size-4 transition-transform',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </Button>
                  )}
                </div>
                {isOpen && g.leaves.length > 0 && (
                  <ul className="border-border/60 mt-0.5 ml-3 space-y-0.5 border-l pl-2">
                    <li>
                      {/* "View All [Group]" — Baymard: literal first row. */}
                      <FacetRow
                        active={groupActive}
                        onClick={() => setParam('category', g.slug)}
                      >
                        {t('explore:marketplace.facets.allInGroup', {
                          group: g.name,
                        })}
                        {g.count > 0 && (
                          <span className="text-muted-foreground ml-1.5 text-xs tabular-nums">
                            {g.count}
                          </span>
                        )}
                      </FacetRow>
                    </li>
                    {g.leaves.map((l) => {
                      const active = activeCategory === l.slug;
                      const disabled = !active && l.count === 0;
                      return (
                        <li key={l.slug}>
                          <Button
                            type="button"
                            variant="ghost"
                            aria-pressed={active}
                            disabled={disabled}
                            onClick={() =>
                              setParam('category', active ? null : l.slug)
                            }
                            className={cn(
                              'w-full min-w-0 justify-start rounded-md px-2 py-1.5 text-left text-sm whitespace-normal transition-colors',
                              active
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                              disabled &&
                                'cursor-not-allowed opacity-40 hover:bg-transparent',
                            )}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <CategoryIcon
                                name={l.iconName ?? null}
                                className="size-4 shrink-0"
                              />
                              <span className="min-w-0 flex-1 break-words">
                                {l.name}
                                <span className="text-muted-foreground ml-1.5 text-xs tabular-nums">
                                  {l.count}
                                </span>
                              </span>
                            </span>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </FacetGroup>

      <FacetGroup
        label={<Trans i18nKey="explore:marketplace.facets.condition" />}
      >
        <div className="flex flex-wrap gap-2">
          {CONDITIONS.map((cond) => {
            const active = activeConditions.includes(cond.value);
            const count = counts?.condition[cond.value];
            const disabled = !active && count === 0;
            return (
              <Button
                key={cond.value}
                type="button"
                variant="ghost"
                aria-pressed={active}
                disabled={disabled}
                onClick={() => toggleMulti('condition', cond.value)}
                className={cn(
                  'rounded-md border px-3 py-1 text-sm transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  disabled &&
                    'hover:border-border cursor-not-allowed opacity-40',
                )}
              >
                {t(cond.labelKey)}
                {count !== undefined && (
                  <span className="text-muted-foreground ml-1.5 text-xs tabular-nums">
                    {count}
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </FacetGroup>

      <FacetGroup label={<Trans i18nKey="explore:marketplace.facets.brand" />}>
        <Input
          type="search"
          value={brand.value}
          onChange={(e) => brand.setValue(e.target.value)}
          placeholder={t('explore:marketplace.facets.brandPlaceholder')}
          aria-label={t('explore:marketplace.facets.brandAriaLabel')}
        />
      </FacetGroup>

      {colors.length > 0 && (
        <FacetGroup
          label={<Trans i18nKey="explore:marketplace.facets.colour" />}
        >
          <div className="flex flex-wrap gap-2">
            {colors.map((color) => {
              const active = activeColors.includes(color);
              const swatch = COLOR_SWATCHES[color.toLowerCase()];
              const count = counts?.color[color];
              const disabled = !active && count === 0;
              return (
                <Button
                  key={color}
                  type="button"
                  variant="ghost"
                  aria-pressed={active}
                  disabled={disabled}
                  title={color}
                  onClick={() => toggleMulti('color', color)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border py-1 pr-2.5 pl-1.5 text-sm capitalize transition-colors',
                    active
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                    disabled &&
                      'hover:border-border cursor-not-allowed opacity-40',
                  )}
                >
                  <span
                    aria-hidden
                    className="border-border/60 size-3.5 rounded-full border"
                    style={
                      swatch
                        ? { backgroundColor: swatch }
                        : {
                            backgroundImage:
                              'linear-gradient(135deg, #ddd 0 50%, #bbb 50% 100%)',
                          }
                    }
                  />
                  {color}
                  {count !== undefined && (
                    <span className="text-muted-foreground ml-0.5 text-xs tabular-nums">
                      {count}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        </FacetGroup>
      )}

      {sizes.length > 0 && (
        <FacetGroup label={<Trans i18nKey="explore:marketplace.facets.size" />}>
          <div className="flex flex-wrap gap-2">
            {sizes.map((size) => {
              const active = activeSizes.includes(size);
              const count = counts?.size[size];
              const disabled = !active && count === 0;
              return (
                <Button
                  key={size}
                  type="button"
                  variant="ghost"
                  aria-pressed={active}
                  disabled={disabled}
                  onClick={() => toggleMulti('size', size)}
                  className={cn(
                    'min-w-9 rounded-md border px-2.5 py-1 text-center text-sm transition-colors',
                    active
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                    disabled &&
                      'hover:border-border cursor-not-allowed opacity-40',
                  )}
                >
                  {size}
                  {count !== undefined && (
                    <span className="text-muted-foreground ml-1 text-xs tabular-nums">
                      {count}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        </FacetGroup>
      )}

      <FacetGroup label={<Trans i18nKey="explore:marketplace.facets.price" />}>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="0"
            inputMode="numeric"
            value={priceMin.value}
            onChange={(e) => priceMin.setValue(e.target.value)}
            placeholder={t('explore:marketplace.facets.minPlaceholder')}
            aria-label={t('explore:marketplace.facets.minAriaLabel')}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="number"
            min="0"
            inputMode="numeric"
            value={priceMax.value}
            onChange={(e) => priceMax.setValue(e.target.value)}
            placeholder={t('explore:marketplace.facets.maxPlaceholder')}
            aria-label={t('explore:marketplace.facets.maxAriaLabel')}
          />
        </div>
      </FacetGroup>
    </div>
  );
}

function FacetGroup({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold">{label}</legend>
      {children}
    </fieldset>
  );
}

function FacetRow({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'w-full min-w-0 justify-start rounded-md px-2 py-1.5 text-left text-sm whitespace-normal transition-colors',
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </Button>
  );
}
